/**
 * Спільна шапка стандартних звітів (ч.1036): організація (лише де є право
 * на регламентні звіти), період, місце для власних фільтрів звіту, дії
 * «друк» / «excel» (Закон дизайну #7: текстом, з малої).
 */
import type { ReactNode } from 'react';
import { useState } from 'react';
import DatePopover from '@/components/DatePopover';
import { showToastImperative } from '@/components/ToastProvider';
import { saveBlob } from '@/shared/utils/download';
import type { LedgerState } from './useLedgerParams';

interface Props {
  state: LedgerState;
  children?: ReactNode;
  /** Відкрити друковану форму (нова вкладка). */
  printHref?: string;
  /** Завантажити Excel: повертає {blob, filename}. */
  onExcel?: () => Promise<{ blob: Blob; filename: string }>;
  disabled?: boolean;
}

export default function LedgerFilters({ state, children, printHref, onExcel, disabled }: Props) {
  const [busy, setBusy] = useState(false);
  const excel = async () => {
    if (!onExcel) return;
    setBusy(true);
    try {
      const { blob, filename } = await onExcel();
      saveBlob(blob, filename);
    } catch {
      showToastImperative({ type: 'error', message: 'Не вдалося сформувати файл Excel' });
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="ledger__filters">
      <div className="field">
        <label>Організація</label>
        <select value={state.org} onChange={(e) => state.set({ org: e.target.value })}>
          {state.availableOrgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
      </div>
      <div className="field">
        <label>З</label>
        <DatePopover value={state.from} onChange={(v) => state.set({ from: v })} />
      </div>
      <div className="field">
        <label>По</label>
        <DatePopover value={state.to} onChange={(v) => state.set({ to: v })} />
      </div>
      {children}
      <div className="ledger__actions">
        {printHref && (
          <a className="btn-link" href={printHref} target="_blank" rel="noreferrer"
            title="Друкована форма у новій вкладці">друк</a>
        )}
        {onExcel && (
          <button type="button" className="btn-link" disabled={disabled || busy} onClick={excel}
            title="Завантажити цей звіт файлом Excel">{busy ? 'формуємо…' : 'excel'}</button>
        )}
      </div>
    </div>
  );
}

/** Сторінка без права на регламентні звіти — одне повідомлення на всі звіти. */
export function NoRegulatedAccess({ title, total }: { title: string; total: number }) {
  return (
    <div className="card">
      <h2 style={{ marginTop: 0 }}>{title}</h2>
      <p className="ledger__warn">
        У вас немає права переглядати регламентні звіти у жодній з ваших
        організацій ({total}). Зверніться до адміністратора.
      </p>
    </div>
  );
}
